<template>
  <div class="courses-search-wrapper max-w-7xl mx-auto px-4 py-8 tracking-wide">
    <header class="mb-6">
      <h1 class="text-3xl font-extralight text-secondary" v-if="searchQuery != null">
        {{ translate('results for', 'ucfirst') }} <span class="font-normal italic">"{{ searchQuery }}"</span>
      </h1>
      <p class="text-sm text-gray-500 mt-1 font-mono">
        {{ courses.length }} {{ courses.length === 1 ? translate('result found') : translate('results found') }}
      </p>
    </header>

    <div class="flex items-center gap-4 bg-surface-50 border border-iron p-4 rounded-md mb-8 text-sm text-gray-600">
      <i class="pi pi-info-circle text-blog-beige text-xl"></i>
      <div>
        {{ translate('Still not sure? All courses include a certificate of completion.') }}
      </div>
    </div>

    <div class="flex flex-col lg:flex-row gap-8">
      
      <aside class="w-full lg:w-1/4 hidden lg:block">
        <div class="sticky top-4">
          <FilterSidebar @filter-change="handleSelectedFilter" />

          <Button class="bg-button mt-5 w-full" :label="translate('search', 'ucfirst')" :icon="'pi pi-search'" @click.prevent="fetchCourses()" />
        </div>
      </aside>


      <main class="w-full lg:w-3/4">
        <div class="flex justify-between items-center mb-6 pb-4 border-b border-iron">
          <button class="lg:hidden px-4 py-2 border border-iron rounded text-secondary font-medium flex items-center gap-2 text-sm bg-white">
            <i class="pi pi-filter"></i> {{ translate('filters', 'ucfirst') }}
          </button>
          
          <div class="hidden lg:flex items-center gap-2 text-sm text-gray-500 ms-auto">
            <span>{{ translate('Sort by:') }}</span>
            <Select 
              v-model="filters.sort" 
              :options="translatedSortOptions" 
              optionLabel="label" 
              optionValue="value" 
              :placeholder="translate('select', 'ucfirst')"
              class="w-48 text-secondary font-medium text-sm border border-iron bg-white"
              pt:root:class="focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div v-if="loading" class="flex justify-center items-center py-20">
          <i class="pi pi-spin pi-spinner text-4xl text-blue-600"></i>
        </div>

        <div v-else-if="courses.length > 0" class="flex flex-col gap-6">
          <CourseRowCard 
            v-for="course in courses" 
            :key="course.id || course.uuid" 
            :course="course"
            :instructor="instructor" 
          />
        </div>

        <div v-else class="text-center py-20 border border-dashed border-iron rounded-md">
          <i class="pi pi-search text-4xl text-gray-300 mb-3 block"></i>
          <p class="text-gray-500 italic">
            {{ translate('No courses matched the search criteria.') }}
          </p>
        </div>
      </main>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, reactive, computed } from 'vue';
import { useRoute } from 'vue-router';
import FilterSidebar from '@/components/Course/FilterSidebar.vue';
import CourseService from '@/services/CourseService';
import CourseRowCard from '@/components/Course/CourseRowCard.vue';
import { Button, Select } from 'primevue';
import { translate } from '@/composables/translate';

const route = useRoute();
const courses = ref([]);
const loading = ref(true);

const searchQuery = ref('');
const filters = reactive({
  languages: [],
  categories: [],
  sort: 'relevance',
  query: null
})

const sortOptions = ref([]);
const translatedSortOptions = computed(() => {
  if (!Array.isArray(sortOptions.value)) return [];
  
  return sortOptions.value.map(opt => ({
    label: translate(opt.label, 'ucfirst'),
    value: opt.value
  }));
});

onMounted(() => {
  loadSortOptions();
  updateQueryTitle();
  fetchCourses();
});

watch(() => route.query, () => {
  updateQueryTitle();
  fetchCourses();
}, { deep: true });

function loadSortOptions() {
  sortOptions.value = CourseService.getSortOptions();
}

function updateQueryTitle() {
  searchQuery.value = route.query.q || route.query.category || translate('all courses', 'capitalize');
}

const fetchCourses = async () => {
  loading.value = true;
  
  courses.value = await CourseService.searchCourseWithFilters(filters);
  
  loading.value = false;
};

/**
 * Callback for when filters from FilterSidebar are selected
 * @param {Object} receivedFilters 
 */
const handleSelectedFilter = (receivedFilters) => {
  filters.languages = receivedFilters.languages || [];
  filters.categories = receivedFilters.categories || []
}
</script>